import React, { useState, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Plus, Shield, Lightbulb } from 'lucide-react';
import { FormField, FormGroup, FormGrid, SubmitButton, useFormContext } from '../common/Form';
import { Role } from '../../services/users';

// Props pour le composant CreateUserFormContent
interface CreateUserFormContentProps {
  filteredRoles: Role[];
  filteredSites: any[];
  allTenants: any[];
  isPlatformAdmin: boolean;
  sitesForSelectedTenant: any[];
  setSitesForSelectedTenant: (sites: any[]) => void;
  isSiteRequiredForRole: (roleName: string) => boolean;
  isTenantRequiredForRole: (roleName: string) => boolean;
  getRoleDisplayName: (role: Role) => string;
  generateUsername: (prenom: string, nom: string) => string;
  isLoading: boolean;
  onCancel: () => void;
}

// Composant séparé pour le contenu du formulaire
const CreateUserFormContent: React.FC<CreateUserFormContentProps> = ({
  filteredRoles,
  filteredSites,
  allTenants,
  isPlatformAdmin,
  sitesForSelectedTenant,
  setSitesForSelectedTenant,
  isSiteRequiredForRole,
  isTenantRequiredForRole,
  getRoleDisplayName,
  generateUsername,
  isLoading,
  onCancel
}) => {
  const { setValue, control } = useFormContext();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const selectedRoleIdRef = useRef<string>('');
  const [watchedTenantId, setWatchedTenantId] = useState<string>('');
  const watchedTenantIdRef = useRef<string>('');
  
  // Référence pour stocker les sites filtrés et éviter les problèmes de dépendances
  const filteredSitesRef = useRef<any[]>([]);
  
  // Mettre à jour la référence quand filteredSites change
  useEffect(() => {
    filteredSitesRef.current = filteredSites;
  }, [filteredSites]);
  
  useEffect(() => {
    const checkRoleSelection = () => {
      const roleSelect = document.querySelector('select[name="role_id"]') as HTMLSelectElement;
      if (roleSelect && roleSelect.value !== selectedRoleIdRef.current) {
        const newRoleId = roleSelect.value;
        selectedRoleIdRef.current = newRoleId;
        setSelectedRoleId(prevRoleId => {
          if (prevRoleId !== newRoleId) {
            return newRoleId;
          }
          return prevRoleId;
        });
      }
    };
    
    checkRoleSelection();
    const interval = setInterval(checkRoleSelection, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Fonction pour gérer les changements de tenant
  const handleTenantChange = (tenantId: string) => {
    console.log('✅ handleTenantChange appelé:', {
      tenantId,
      tenantIdType: typeof tenantId,
      tenantIdLength: tenantId?.length,
      watchedTenantIdRef: watchedTenantIdRef.current,
      watchedTenantIdRefType: typeof watchedTenantIdRef.current,
      watchedTenantId,
      watchedTenantIdType: typeof watchedTenantId,
      filteredSitesCount: filteredSitesRef.current.length,
      allTenantsCount: allTenants.length
    });
    
    if (tenantId === watchedTenantIdRef.current) {
      console.log('⚠️ Valeur identique, pas de mise à jour');
      return;
    }
    
    console.log('🔄 Mise à jour du tenant:', {
      ancien: watchedTenantIdRef.current,
      nouveau: tenantId
    });
    
    watchedTenantIdRef.current = tenantId;
    setWatchedTenantId(tenantId);
    
    // Réinitialiser la sélection du site quand le tenant change
    setValue('default_site_id', '', { 
      shouldValidate: false, 
      shouldDirty: true,
      shouldTouch: false
    });
    
    console.log('🔄 Tenant changé, sites seront filtrés dans useEffect');
  };
  
  // Surveiller les changements de tenant sélectionné pour mettre à jour les sites
  useEffect(() => {
    console.log('🔄 useEffect watchedTenantId DÉCLENCHÉ:', {
      watchedTenantId,
      watchedTenantIdType: typeof watchedTenantId,
      watchedTenantIdLength: watchedTenantId?.length,
      filteredSitesCount: filteredSitesRef.current.length,
      filteredSites: filteredSitesRef.current.map(s => ({ 
        id: s.id, 
        name: s.name, 
        tenant_id: s.tenant?.id,
        tenant_object: s.tenant 
      }))
    });
    
    if (watchedTenantId && watchedTenantId.trim() !== '') {
      console.log('🔍 Début du filtrage pour tenant:', watchedTenantId);
      
      const tenantSites = filteredSitesRef.current.filter(site => {
        const siteTenantId = site.tenant?.id;
        const selectedTenantIdNum = parseInt(watchedTenantId);
        const match = siteTenantId === selectedTenantIdNum;
        
        console.log('🔍 Analyse site:', {
          siteId: site.id,
          siteName: site.name,
          siteTenantId,
          siteTenantIdType: typeof siteTenantId,
          selectedTenantId: watchedTenantId,
          selectedTenantIdNum,
          selectedTenantIdNumType: typeof selectedTenantIdNum,
          match,
          siteTenantObject: site.tenant
        });
        
        return match;
      });
      
      console.log('✅ Résultat du filtrage:', {
        tenantId: watchedTenantId,
        sitesCount: tenantSites.length,
        sites: tenantSites.map(s => ({ 
          id: s.id, 
          name: s.name, 
          tenant_id: s.tenant?.id,
          tenant_name: s.tenant?.name 
        }))
      });
      
      setSitesForSelectedTenant(tenantSites);
    } else {
      console.log('⚠️ Aucun tenant sélectionné ou tenant vide, vidage des sites');
      setSitesForSelectedTenant([]);
    }
  }, [watchedTenantId, setSitesForSelectedTenant]);
  
  // Surveiller les changements de rôle pour afficher/masquer le champ site
  const selectedRole = filteredRoles.find(role => role.id === selectedRoleId);
  const showSiteFieldForRole = selectedRole && isSiteRequiredForRole(selectedRole.name);
  const showSiteField = showSiteFieldForRole;
  const showTenantFieldForRole = selectedRole && isTenantRequiredForRole(selectedRole.name);
  const showTenantFieldConditional = isPlatformAdmin && showTenantFieldForRole;
  
  // Composant pour le champ de sélection du rôle avec gestion d'état
  const RoleSelectField: React.FC = () => {
    return (
      <FormField
        name="role_id"
        label="Rôle"
        type="select"
        options={(() => {
          if (filteredRoles.length === 0) {
            return [{ value: '', label: 'Aucun rôle disponible' }];
          }
          
          // Organiser les rôles par catégories
          const roleCategories = {
            'Gestion de la plateforme': ['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
            'Gestion tenant': ['TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER'],
            'Gestion sites': ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'],
            'Opérationnels': ['ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER', 'STORE_MANAGER']
          };
          
          const organizedOptions: { value: string; label: string }[] = [];
          let separatorIndex = 0;
          
          Object.entries(roleCategories).forEach(([categoryName, roleNames]) => {
            const categoryRoles = filteredRoles.filter(role => roleNames.includes(role.name));
           
            if (categoryRoles.length > 0) {
              // Ajouter un séparateur de catégorie avec une clé unique
              organizedOptions.push({
                value: `separator_${separatorIndex}`,
                label: `── ${categoryName} ──`
              });
              separatorIndex++;
              
              // Ajouter les rôles de cette catégorie
              categoryRoles.forEach(role => {
                organizedOptions.push({
                  value: String(role.id),
                  label: `  ${getRoleDisplayName(role)}`
                });
              });
            }
          });
          
          return organizedOptions;
        })()}
        required
        helpText="Le rôle détermine les permissions de l'utilisateur"
      />
    );
  };
  
  // Gestionnaire pour mettre à jour le nom d'utilisateur quand le prénom ou le nom change
  const handleNameChange = () => {
    // Utiliser les valeurs actuelles des champs via les refs ou un état local
    const prenomValue = (document.querySelector('input[name="prenom"]') as HTMLInputElement)?.value || '';
    const nomValue = (document.querySelector('input[name="nom"]') as HTMLInputElement)?.value || '';
    
    if (prenomValue && nomValue) {
      const newSuggestedUsername = generateUsername(prenomValue, nomValue);
      if (newSuggestedUsername) {
        // Utiliser setValue de React Hook Form pour mettre à jour la valeur
        setValue('username', newSuggestedUsername, { 
          shouldValidate: true, 
          shouldDirty: true,
          shouldTouch: true
        });
        
        // Mettre à jour également la valeur du champ DOM pour l'affichage
        const usernameField = document.querySelector('input[name="username"]') as HTMLInputElement;
        if (usernameField) {
          usernameField.value = newSuggestedUsername;
        }
      }
    }
  };

  return (
    <>
      {/* Section Identité */}
      <FormGroup
        title="Identité"
        description="Informations de base de l'utilisateur"
      >
        <FormGrid cols={2}>
          <FormField
            name="prenom"
            label="Prénom"
            placeholder="Prénom"
            required
          />
          <FormField
            name="nom"
            label="Nom de famille"
            placeholder="Nom de famille"
            required
          />
        </FormGrid>
        
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            
            <div className="flex-1">
              <FormField
                name="username"
                label="Nom d'utilisateur"
                placeholder="prenom.nom"
                helpText="Format recommandé: prenom.nom (min 5 caractères, lettres/chiffres/points uniquement, structure libre)"
                required
              />
            </div>
            <button
              type="button"
              onClick={handleNameChange}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300 transition-colors whitespace-nowrap"
              title="Générer un nom d'utilisateur basé sur le prénom et le nom"
            >
              <Lightbulb className="h-4 w-4" />
              Suggérer
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Lightbulb className="h-4 w-4" />
            <span>
              <strong>Conseil :</strong> Le format <code className="bg-blue-100 px-1 rounded">prenom.nom</code> 
              est recommandé mais non obligatoire (ex: <code className="bg-blue-100 px-1 rounded">jean.dupont</code>)
            </span>
          </div>
        </div>
        
        <FormGrid cols={2}>
          <FormField
            name="email"
            label="Email"
            type="email"
            placeholder="email@exemple.com"
            helpText="Optionnel - pour notifications et récupération de compte"
          />
          <FormField
            name="phone"
            label="Téléphone"
            type="tel"
            placeholder="+225 01 23 45 67 89"
            helpText="Optionnel"
          />
        </FormGrid>
        
        <FormField
          name="locale"
          label="Langue"
          type="select"
          options={[
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
            { value: 'es', label: 'Español' }
          ]}
          required
        />
      </FormGroup>

      {/* Section Rôle */}
      <FormGroup
        title="Rôle"
        description="Sélectionner le rôle de l'utilisateur"
      >
        <RoleSelectField />
        
        {/* Affichage conditionnel du champ tenant */}
        {showTenantFieldConditional && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tenant *
            </label>
            <Controller
              name="tenant_id"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    handleTenantChange(value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Sélectionner un tenant</option>
                  {allTenants.map(tenant => (
                    <option key={tenant.id} value={String(tenant.id)}>
                      {tenant.name || 'Tenant sans nom'} ({tenant.code || 'N/A'})
                    </option>
                  ))}
                </select>
              )}
            />
            <p className="mt-1 text-sm text-gray-500">
              Sélectionnez le tenant pour lequel créer l'utilisateur
            </p>
          </div>
        )}
        
        {/* Affichage conditionnel du champ site par défaut */}
        {showSiteFieldForRole && (
          <div className="mt-4">
            <FormField
              name="default_site_id"
              label="Site par défaut"
              type="select"
              options={(() => {
                // Utiliser les sites filtrés selon le contexte
                let sitesToShow = [];
                console.log('🔍 Logique d\'affichage des sites DÉTAILLÉE:', {
                  showTenantFieldConditional,
                  watchedTenantId,
                  watchedTenantIdType: typeof watchedTenantId,
                  sitesForSelectedTenantCount: sitesForSelectedTenant.length,
                  sitesForSelectedTenant: sitesForSelectedTenant.map(s => ({ 
                    id: s.id, 
                    name: s.name, 
                    tenant_id: s.tenant?.id 
                  })),
                  filteredSitesCount: filteredSites.length,
                  filteredSites: filteredSites.map(s => ({ 
                    id: s.id, 
                    name: s.name, 
                    tenant_id: s.tenant?.id 
                  }))
                });
                
                if (showTenantFieldConditional && watchedTenantId) {
                  // Si le champ tenant est affiché et qu'un tenant est sélectionné, utiliser les sites filtrés
                  sitesToShow = sitesForSelectedTenant;
                  console.log('✅ Utilisation des sites filtrés par tenant:', {
                    count: sitesToShow.length,
                    sites: sitesToShow.map(s => ({ id: s.id, name: s.name, tenant_id: s.tenant?.id }))
                  });
                } else {
                  // Par défaut, utiliser tous les sites disponibles
                  sitesToShow = filteredSites;
                  console.log('✅ Utilisation de tous les sites disponibles:', {
                    count: sitesToShow.length,
                    sites: sitesToShow.map(s => ({ id: s.id, name: s.name, tenant_id: s.tenant?.id }))
                  });
                }
                
                console.log('🔍 État final de sitesToShow:', {
                  sitesToShowLength: sitesToShow.length,
                  sitesToShow: sitesToShow.map(s => ({ 
                    id: s.id, 
                    name: s.name, 
                    tenant_id: s.tenant?.id,
                    tenant_name: s.tenant?.name 
                  }))
                });
               
                const options = sitesToShow.length > 0 ? sitesToShow.map(site => ({
                  value: String(site.id || ''),
                  label: `${site.name || 'Site sans nom'}${site.code ? ` (${site.code})` : ''}`
                })) : [{ value: '', label: 'Aucun site disponible' }];
                
                console.log('🎯 Options finales générées:', {
                  optionsCount: options.length,
                  options: options
                });
                
                return options;
              })()}
                required
                helpText={`Site par défaut obligatoire pour le rôle "${selectedRole ? getRoleDisplayName(selectedRole) : 'sélectionné'}"`}
              />
          </div>
        )}
        
        {/* Message informatif quand le site n'est pas requis */}
        {selectedRole && !showSiteField && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <Shield className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-sm text-green-800">
                Le rôle <strong>{getRoleDisplayName(selectedRole)}</strong> ne nécessite pas d'assignation de site par défaut. L'utilisateur pourra être assigné à des sites ultérieurement.
              </span>
            </div>
          </div>
        )}
        
      </FormGroup>

      {/* Boutons d'action */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 shadow-sm hover:shadow transition-all"
        >
          Annuler
        </button>
        
        <SubmitButton
          isLoading={isLoading}
          className="bg-orange-600 hover:bg-orange-700 focus:ring-orange-500"
        >
          <Plus className="h-4 w-4" />
          Créer l'utilisateur
        </SubmitButton>
      </div>
    </>
  );
};

export default CreateUserFormContent;
